import { useEffect, useState } from 'react';

import {
  type FrequencyType,
  type GroupClassIllustrations,
  ChakraBox,
  EmptySectionCard,
  FAIconName,
  GroupClassListViewCard,
  InfiniteScroll,
  SkeletonLoader,
  useToastMessage,
} from '@equip.health/ui';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { GroupClassEvents } from '~/analytics/groupClassEvents';
import { AnalyticsSourceValues } from '~/lib/constants/AnalyticsSource.constants';
import {
  type GroupClassListTab,
  GET_GROUP_CLASS_PAGE_SIZE_V2,
  GroupClassListTypeEnum,
} from '~/lib/constants/groupClasses.constants';
import { MaudPermissions } from '~/lib/constants/permissions.constants';
import { useAnalytics } from '~/lib/contexts/AnalyticsContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  useLazyGetActiveOrArchivedGroupClassesListQuery,
  useLazyGetGroupClassDetailQuery,
  useLazyGetMyGroupClassesListQuery,
  useRegisterForGroupClassMutation,
  useUnregisterForGroupClassMutation,
} from '~/store/services/groupClass';

import GroupClassArchiveModal from './modals/ArchiveGroupModal';
import GroupClassDetailsModal from './modals/GroupClassDetailsModal';
import type { CalendarScheduleTabProps } from '~/components/common/calendar/schedule/ScheduleChangerV2';
import { getEmptyCaption } from '~/lib/util/task.util';
import { getDisplayNameOrLegalNameForProvider } from '~/lib/util/user.util';
import ProviderAvatarTooltipWrapper from '~/components/common/ProviderAvatarBlockTooltipWrapper';
import type { GroupClassKebabItem } from '~/lib/types/classes/GroupClassKebabItem';

export const GroupClassKebabMenuTitle: Record<
  GroupClassKebabMenuOption,
  string
> = {
  ['archive']: 'Archive this group',
  ['edit']: 'Edit this group',
  ['register']: 'Register for this group',
  ['unregister']: 'Unregister from this group',
  ['view']: 'View group details',
};

interface GroupClassProp {
  endDate?: string;
  frequency: RecurrenceFrequencyValue;
  groupClassExternalId: string;
  hosts: GroupClassHost[];
  iconName: string;
  isUserRegistered: boolean;
  isSensitive?: boolean;
  isArchived?: boolean;
  isDisabled?: boolean;
  isHost?: boolean;
  kebabItems: GroupClassKebabItem[];
  nextStartDate: string;
  startDate: string;
  title: string;
}

const GroupClassListView = ({
  handleResetReload,
  listType,
  needReload,
  tabs,
  groupClassTitle,
}: {
  handleResetReload: () => void;
  listType: GroupClassListType;
  needReload: boolean;
  tabs: Record<GroupClassListTab, CalendarScheduleTabProps>;
  groupClassTitle: string;
}) => {
  const TOP_LEVEL_ID = 'group-class__list';

  const { permissions } = useGlobalContext();
  const { createToast } = useToastMessage();
  const { setComponentVisibility } = useComponentPortalContext();
  const {
    groupsHideProviderRegistration: isGroupsHideProviderRegistrationEnabled,
  } = useFlags<LDFlags>();

  const isSchedulingAdmin = permissions[MaudPermissions.MAUD_SCHEDULE_PROXY];

  const [registerForGroupClass] = useRegisterForGroupClassMutation();
  const [unregisterForGroupClass] = useUnregisterForGroupClassMutation();
  const [getActiveOrArchiveGroupClassesList] =
    useLazyGetActiveOrArchivedGroupClassesListQuery();
  const [getMyGroupsClassesList] = useLazyGetMyGroupClassesListQuery();

  const [getGroupClassDetail] = useLazyGetGroupClassDetailQuery();

  const [selectedGroupClassExternalId, setSelectedGroupClassExternalId] =
    useState<string>();

  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState<boolean>(false);
  const [isGroupClassDetailModalOpen, setIsGroupClassDetailModalOpen] =
    useState<boolean>(false);
  const [groupClassList, setGroupClassList] = useState<GroupClassBaseV2[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const [groupClass, setGroupClass] = useState<GroupClassExtended>();

  const [currentListType, setCurrentListType] = useState<GroupClassListType>();

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  type Modal = 'modal' | 'list';

  const handleToast = () => {
    createToast({
      icon: FAIconName.exclamationTriangleRegular,
      id: `${TOP_LEVEL_ID}__warning`,
      text: "We're sorry, we were unable process your request at this time. Please try again.",
      variant: 'critical',
    });
  };

  const onFetchGroupClassesSuccess = ({
    groupClassListResponse,
    page,
  }: {
    groupClassListResponse: GroupClassBaseV2[];
    page?: number;
  }) => {
    setGroupClassList(
      page
        ? groupClassListResponse
        : [...groupClassList, ...groupClassListResponse],
    );
    setPageNumber((page ?? pageNumber) + 1);
  };

  const fetchActiveOrArchiveGroupClassesList = async ({
    isActive,
    page,
  }: {
    isActive: boolean;
    page?: number;
  }) => {
    getActiveOrArchiveGroupClassesList({
      isActive,
      pageNumber: page ?? pageNumber,
      title: groupClassTitle,
    })
      .unwrap()
      .then(({ data: groupClassListResponse }) =>
        onFetchGroupClassesSuccess({
          groupClassListResponse,
          page,
        }),
      )
      .catch(handleToast)
      .finally(() => setIsDataLoaded(true));
  };

  const fetchMyGroupsClassesList = ({ page }: { page?: number }) => {
    getMyGroupsClassesList({
      pageNumber: page ?? pageNumber,
      title: groupClassTitle,
    })
      .unwrap()
      .then(({ data: groupClassListResponse }) =>
        onFetchGroupClassesSuccess({
          groupClassListResponse,
          page,
        }),
      )
      .catch(handleToast)
      .finally(() => setIsDataLoaded(true));
  };

  const changeRegistrationStatusForActiveGroupClass = (
    groupClassExternalId: string,
  ) => {
    setGroupClassList((prevData) =>
      prevData.map((groupClass) =>
        groupClass.groupClassExternalId === groupClassExternalId
          ? { ...groupClass, isUserRegistered: !groupClass.isUserRegistered }
          : groupClass,
      ),
    );
  };

  const removeGroupClassFromMyGroupsList = (groupClassExternalId: string) => {
    setGroupClassList((prevData) =>
      prevData.filter(
        (groupClass) =>
          groupClass.groupClassExternalId !== groupClassExternalId,
      ),
    );
  };

  const updateRegisteredTagInActiveGroups = (groupClassExternalId: string) => {
    if (groupClassExternalId) {
      if (listType === GroupClassListTypeEnum.ACTIVE) {
        changeRegistrationStatusForActiveGroupClass(groupClassExternalId);
      } else if (listType === GroupClassListTypeEnum.MY_GROUPS) {
        removeGroupClassFromMyGroupsList(groupClassExternalId);
      }
    }

    if (groupClass) {
      setGroupClass((prevGroupClass) => ({
        ...prevGroupClass,
        isUserRegistered: !prevGroupClass.isUserRegistered,
      }));
    }
  };

  const getGroupClassesFromStart = () => {
    setIsDataLoaded(false);
    setGroupClassList([]);
    const isActive = listType === GroupClassListTypeEnum.ACTIVE;
    if (listType === GroupClassListTypeEnum.MY_GROUPS) {
      fetchMyGroupsClassesList({ page: 1 });
    } else {
      fetchActiveOrArchiveGroupClassesList({ isActive, page: 1 });
    }
  };

  if (listType !== currentListType) {
    setIsDataLoaded(false);
    setGroupClassList([]);
    getGroupClassesFromStart();
    setCurrentListType(listType);
  }

  if (needReload) {
    handleResetReload();
    getGroupClassesFromStart();
    setIsDataLoaded(false);
  }

  useEffect(() => {
    getGroupClassesFromStart();
  }, [groupClassTitle]);

  const loadMoreItems = () => {
    const isActive = listType === GroupClassListTypeEnum.ACTIVE;
    if (listType == GroupClassListTypeEnum.MY_GROUPS) {
      if (groupClassList.length >= GET_GROUP_CLASS_PAGE_SIZE_V2) {
        fetchMyGroupsClassesList({});
      }
    } else {
      fetchActiveOrArchiveGroupClassesList({ isActive });
    }
  };

  const showGroupClassDetail = (groupClassExternalId: string) => {
    getGroupClassDetail({ groupClassExternalId })
      .unwrap()
      .then(({ data: groupClassDetail }) => {
        setGroupClass(groupClassDetail);
        setIsGroupClassDetailModalOpen(true);
      })
      .catch(handleToast);
  };

  const handleSuccessfulGroupClassEdit = ({
    id,
    isViewClass,
  }: {
    id: string;
    isViewClass: boolean;
  }) => {
    if (isViewClass) {
      showGroupClassDetail(id);
    } else {
      getGroupClassesFromStart();
    }
  };

  const handleEditModal = (selectedGroupClass: GroupClassExtended) => {
    const data: EditGroupClassData = {
      handleSuccessfulGroupClassEdit,
      selectedGroupClass,
    };
    setComponentVisibility({
      component: PortalComponent.EDIT_GROUP_CLASS_ACCORDION,
      data,
      isExpanded: true,
      isVisible: true,
    });
  };

  const handleKebabOption = async (
    groupClassExternalId: string,
    option: string,
  ) => {
    switch (option) {
      case GroupClassKebabMenuTitle.view:
        showGroupClassDetail(groupClassExternalId);
        break;
      case GroupClassKebabMenuTitle.register:
        registerForGroupClass({
          groupClassExternalId,
        })
          .unwrap()
          .then(() => {
            updateRegisteredTagInActiveGroups(groupClassExternalId);
          })
          .catch(handleToast);

        break;
      case GroupClassKebabMenuTitle.unregister:
        unregisterForGroupClass({
          groupClassExternalId,
        })
          .unwrap()
          .then(() => {
            updateRegisteredTagInActiveGroups(groupClassExternalId);
          })
          .catch(handleToast);
        break;
      case GroupClassKebabMenuTitle.edit:
        getGroupClassDetail({ groupClassExternalId })
          .unwrap()
          .then(({ data: groupClassDetail }) => {
            setIsGroupClassDetailModalOpen(false);
            handleEditModal(groupClassDetail);
          })
          .catch(handleToast);
        break;
      case GroupClassKebabMenuTitle.archive:
        setSelectedGroupClassExternalId(groupClassExternalId);
        setIsArchiveModalOpen(true);
        break;
    }
  };

  const { trackEvent } = useAnalytics();

  const viewDetailsAnalyticsTrack = (source?: Modal) => {
    let eventToTrack;
    const analyticsSource = AnalyticsSourceValues.CALENDAR_GROUPS_TAB;
    switch (listType) {
      case GroupClassListTypeEnum.MY_GROUPS:
        eventToTrack =
          source !== 'list'
            ? GroupClassEvents.myGroupsListCardClicked
            : GroupClassEvents.myGroupsListKebabMenuViewButtonClicked;
        trackEvent(eventToTrack, {
          Source: analyticsSource,
        });
        break;
      case GroupClassListTypeEnum.ACTIVE:
        eventToTrack =
          source !== 'list'
            ? GroupClassEvents.activeGroupsListCardButtonClicked
            : GroupClassEvents.activeGroupsListKebabMenuViewButtonClicked;
        trackEvent(eventToTrack, {
          Source: analyticsSource,
        });
        break;
      case GroupClassListTypeEnum.ARCHIVE:
        eventToTrack =
          source !== 'list'
            ? GroupClassEvents.archiveGroupsListCardButtonClicked
            : GroupClassEvents.archiveGroupsListKebabMenuViewButtonClicked;
        trackEvent(eventToTrack, {
          Source: analyticsSource,
        });
        break;
    }
  };

  const getKebabMenuOptions = (details: GroupClassBaseV2, source?: Modal) => {
    let kebabItems: Array<GroupClassKebabItem> = [];
    let eventToTrack;
    const analyticsSource = AnalyticsSourceValues.CALENDAR_GROUPS_TAB;
    switch (listType) {
      case GroupClassListTypeEnum.ACTIVE:
        if (!details.isHost && details.isUserRegistered) {
          kebabItems.push({
            label: GroupClassKebabMenuTitle.unregister,
            leftIcon: FAIconName.timesRegular,
            leftIconColor: 'neutral.primary',
            onClick: () => {
              handleKebabOption(
                details.groupClassExternalId,
                GroupClassKebabMenuTitle.unregister,
              );
              eventToTrack =
                source === 'modal'
                  ? GroupClassEvents.activeGroupsListDetailModalKebabMenuUnRegisterButtonClicked
                  : GroupClassEvents.activeGroupsListKebabMenuUnRegisterButtonClicked;
              trackEvent(eventToTrack, {
                Source: analyticsSource,
              });
            },
          });
        } else if (
          !details.isHost &&
          !isGroupsHideProviderRegistrationEnabled
        ) {
          kebabItems.push({
            label: GroupClassKebabMenuTitle.register,
            leftIcon: FAIconName.checkRegular,
            leftIconColor: 'neutral.primary',
            onClick: () => {
              handleKebabOption(
                details.groupClassExternalId,
                GroupClassKebabMenuTitle.register,
              );
              eventToTrack =
                source === 'modal'
                  ? GroupClassEvents.activeGroupsListDetailModalKebabMenuRegisterButtonClicked
                  : GroupClassEvents.activeGroupsListKebabMenuRegisterButtonClicked;
              trackEvent(eventToTrack, {
                Source: analyticsSource,
              });
            },
          });
        }
        if (isSchedulingAdmin) {
          kebabItems = [
            ...kebabItems,
            {
              label: GroupClassKebabMenuTitle.edit,
              leftIcon: FAIconName.penLine,
              leftIconColor: 'neutral.primary',
              onClick: () => {
                handleKebabOption(
                  details.groupClassExternalId,
                  GroupClassKebabMenuTitle.edit,
                );

                eventToTrack =
                  source === 'modal'
                    ? GroupClassEvents.activeGroupsListDetailModalKebabMenuEditButtonClicked
                    : GroupClassEvents.activeGroupsListKebabMenuEditButtonClicked;

                trackEvent(eventToTrack, {
                  Source: analyticsSource,
                });
              },
            },
            {
              label: GroupClassKebabMenuTitle.archive,
              leftIcon: FAIconName.trashRegular,
              leftIconColor: 'neutral.primary',
              onClick: () => {
                handleKebabOption(
                  details.groupClassExternalId,
                  GroupClassKebabMenuTitle.archive,
                );
                eventToTrack =
                  source === 'modal'
                    ? GroupClassEvents.activeGroupsListDetailModalKebabMenuArchiveButtonClicked
                    : GroupClassEvents.activeGroupsListKebabMenuArchiveButtonClicked;

                trackEvent(eventToTrack, {
                  Source: analyticsSource,
                });
              },
            },
          ];
        }
        break;
      case GroupClassListTypeEnum.MY_GROUPS:
        if (!details.isHost) {
          kebabItems.push({
            label: GroupClassKebabMenuTitle.unregister,
            leftIcon: FAIconName.timesRegular,
            leftIconColor: 'neutral.primary',
            onClick: () => {
              handleKebabOption(
                details.groupClassExternalId,
                GroupClassKebabMenuTitle.unregister,
              );
              eventToTrack =
                source === 'modal'
                  ? GroupClassEvents.myGroupsListDetailModalKebabMenuUnRegisterButtonClicked
                  : GroupClassEvents.myGroupsListKebabMenuUnRegisterButtonClicked;
              trackEvent(eventToTrack, {
                Source: analyticsSource,
              });
            },
          });
        }
        if (isSchedulingAdmin) {
          kebabItems = [
            ...kebabItems,
            {
              label: GroupClassKebabMenuTitle.edit,
              leftIcon: FAIconName.penLine,
              leftIconColor: 'neutral.primary',
              onClick: () => {
                handleKebabOption(
                  details.groupClassExternalId,
                  GroupClassKebabMenuTitle.edit,
                );
                eventToTrack =
                  source === 'modal'
                    ? GroupClassEvents.myGroupsListDetailModalKebabMenuEditButtonClicked
                    : GroupClassEvents.myGroupsListKebabMenuEditButtonClicked;

                trackEvent(eventToTrack, {
                  Source: analyticsSource,
                });
              },
            },
            {
              label: GroupClassKebabMenuTitle.archive,
              leftIcon: FAIconName.trashRegular,
              leftIconColor: 'neutral.primary',
              onClick: () => {
                handleKebabOption(
                  details.groupClassExternalId,
                  GroupClassKebabMenuTitle.archive,
                );
                eventToTrack =
                  source === 'modal'
                    ? GroupClassEvents.myGroupsListDetailModalKebabMenuArchiveButtonClicked
                    : GroupClassEvents.myGroupsListKebabMenuArchiveButtonClicked;

                trackEvent(eventToTrack, {
                  Source: analyticsSource,
                });
              },
            },
          ];
        }
    }
    return kebabItems;
  };

  const getHostDetails = (details: GroupClassBaseV2) => {
    const hosts: Array<GroupClassHost> = details.hosts.map((each) => {
      return {
        ...each,
        name: getDisplayNameOrLegalNameForProvider(
          each.displayFirstName,
          each.firstName,
          each.displayLastName,
          each.lastName,
        ),
        userType: each.type,
        src: each?.avatar?.thumbnailUrl ?? each?.avatar?.url,
      };
    });
    return hosts;
  };

  const getGroupClassCardProps = (classDetails: GroupClassBaseV2) => {
    const viewGroupClassDetail: GroupClassKebabItem = {
      label: GroupClassKebabMenuTitle.view,
      leftIcon: FAIconName.infoCircleRegular,
      leftIconColor: 'neutral.primary',
      onClick: () => {
        handleKebabOption(
          classDetails.groupClassExternalId,
          GroupClassKebabMenuTitle.view,
        );
        viewDetailsAnalyticsTrack('list');
      },
    };

    const cardProps: GroupClassProp = {
      ...classDetails,
      frequency: classDetails.frequency as RecurrenceFrequencyValue,
      hosts: getHostDetails(classDetails),
      isDisabled: classDetails.isArchived,
      kebabItems: [viewGroupClassDetail, ...getKebabMenuOptions(classDetails)],
    };
    return cardProps;
  };

  const renderGroupClassListViewCards = () =>
    groupClassList.map((groupClass: GroupClassBaseV2) => {
      const prop = getGroupClassCardProps(groupClass);
      const hostsWithId = prop.hosts.map((h) => ({
        ...h,
        id: h.externalId,
      }));

      return (
        <ChakraBox key={groupClass.groupClassExternalId} padding="6px">
          <GroupClassListViewCard
            endDate={prop.endDate}
            frequency={prop.frequency as FrequencyType}
            groupClassExternalId={prop.groupClassExternalId}
            hosts={hostsWithId as any} // TODO add userType to this list, to make avatar color scheme work properly OR remove required userType prop from avatar since it *does* have a default
            iconName={prop.iconName as GroupClassIllustrations} // TODO investigate if this should be typed as such higher up, for example at network call level
            id={`group-class-list-card-${prop.groupClassExternalId}`}
            isArchived={prop.isArchived}
            isHost={prop.isHost}
            isSensitive={prop.isSensitive}
            isUserRegistered={prop.isUserRegistered}
            kebabItems={prop.kebabItems}
            nextStartDate={prop.nextStartDate}
            onCardClick={() => {
              showGroupClassDetail(prop.groupClassExternalId);
              viewDetailsAnalyticsTrack();
            }}
            startDate={prop.startDate}
            title={prop.title}
            tooltipWrapper={({ children, providerExternalId }) => (
              <ProviderAvatarTooltipWrapper
                actionTime={prop.startDate}
                isRoleAtAction
                placement="left"
                providerExternalId={providerExternalId}
              >
                {children}
              </ProviderAvatarTooltipWrapper>
            )}
          />
        </ChakraBox>
      );
    });

  const handleRegisterFromGroupClassDetailModal = () => {
    showGroupClassDetail(groupClass.groupClassExternalId);
    updateRegisteredTagInActiveGroups(groupClass.groupClassExternalId);
    if (listType === GroupClassListTypeEnum.MY_GROUPS) {
      getGroupClassesFromStart();
    }
  };

  const handleSuccessfulArchival = () => {
    setIsDataLoaded(false);
    getGroupClassesFromStart();
    setIsGroupClassDetailModalOpen(false);
  };

  return (
    <>
      <ChakraBox height="100%" id={TOP_LEVEL_ID} overflow="auto">
        <ChakraBox
          id={`${TOP_LEVEL_ID}__group-class-list-container`}
          marginRight="24px"
          paddingBottom="36px"
        >
          <SkeletonLoader
            id={`${TOP_LEVEL_ID}__skeleton-loader`}
            isLoaded={isDataLoaded}
          >
            {groupClassList?.length > 0 ? (
              <InfiniteScroll
                dataLength={groupClassList.length ?? 0}
                fetchData={loadMoreItems}
                hideCount
                id={`${TOP_LEVEL_ID}__infinite-scroll`}
                scrollableTarget={TOP_LEVEL_ID}
                showLoader
                totalCount={tabs?.[listType]?.count}
              >
                {renderGroupClassListViewCards()}
              </InfiniteScroll>
            ) : (
              <EmptySectionCard
                caption={getEmptyCaption(groupClassTitle, listType)}
                id={`${TOP_LEVEL_ID}__empty-card`}
              />
            )}
          </SkeletonLoader>
        </ChakraBox>

        {groupClass && (
          <GroupClassDetailsModal
            groupClass={groupClass}
            isOpen={isGroupClassDetailModalOpen}
            kebabOptions={getKebabMenuOptions(groupClass, 'modal')}
            onClose={() => setIsGroupClassDetailModalOpen(false)}
            onRegistration={handleRegisterFromGroupClassDetailModal}
          />
        )}
      </ChakraBox>

      <GroupClassArchiveModal
        groupClassExternalId={selectedGroupClassExternalId}
        isOpen={isArchiveModalOpen}
        onClose={() => setIsArchiveModalOpen(false)}
        onSuccess={handleSuccessfulArchival}
      />
    </>
  );
};
export default GroupClassListView;
